import { useQuery } from '@tanstack/react-query';

import type { CurrentUserResponse, PharosApiResponse } from '@klareospace/pharos-lib-types';

import { useApiClient } from '../api';
import { ADMIN_AUTH_API_CLIENT_NAME } from '../constants';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const currentUserKeys = {
  all: ['current-user'] as const,
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseCurrentUserReturn {
  /** Whether the user profile is currently being fetched */
  isLoading: boolean;
  /** The current user from the backend (with resolved scopes), or null if not yet loaded */
  user: CurrentUserResponse | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCurrentUser(isAuthenticated = true): UseCurrentUserReturn {
  const client = useApiClient(ADMIN_AUTH_API_CLIENT_NAME);

  const { data, isLoading } = useQuery({
    enabled: isAuthenticated,
    queryFn: async (): Promise<CurrentUserResponse> => {
      const response = await client.execute<PharosApiResponse<CurrentUserResponse>>(() =>
        client.instance.get('/users/me'),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from /users/me');
      }
      return response.data;
    },
    queryKey: currentUserKeys.all,
    staleTime: 30 * 60 * 1000, // 30 minutes — user profile rarely changes mid-session
  });

  return {
    isLoading,
    user: data ?? null,
  };
}
